import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Globe, Users, Package } from 'lucide-react';
import { SEOHead, getOrganizationSchema, getLocalBusinessSchema } from '../components/common/SEOHead';
import { CategoryCard } from '../components/common/CategoryCard';
import { ProductCard } from '../components/common/ProductCard';
import { BrandCard } from '../components/common/BrandCard';
import { LeadForm } from '../components/common/LeadForm';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getCategories, getProducts, getBrands } from '../lib/api';
import { dedupeBrandsBySlug } from '../lib/utils';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1758871993077-e084cc7eca86?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85';
const ABOUT_IMAGE = `${process.env.PUBLIC_URL}/about_us.png`;

const BRAND_GRID_ROWS_COLLAPSED = 2;

function getBrandGridColumnCount() {
  if (typeof window === 'undefined') return 3;
  if (window.matchMedia('(min-width: 1024px)').matches) return 8;
  if (window.matchMedia('(min-width: 768px)').matches) return 6;
  if (window.matchMedia('(min-width: 640px)').matches) return 4;
  return 3;
}

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [brandGridCols, setBrandGridCols] = useState(() =>
    typeof window !== 'undefined' ? getBrandGridColumnCount() : 3,
  );

  useEffect(() => {
    const updateCols = () => setBrandGridCols(getBrandGridColumnCount());
    updateCols();
    window.addEventListener('resize', updateCols);
    return () => window.removeEventListener('resize', updateCols);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes, brandRes] = await Promise.all([
          getCategories(),
          getProducts({ featured: true, limit: 8 }),
          getBrands(),
        ]);
        setCategories(catRes.data);
        setFeaturedProducts(prodRes.data);
        setBrands(dedupeBrandsBySlug(brandRes.data || []));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const brandPreviewCount = brandGridCols * BRAND_GRID_ROWS_COLLAPSED;
  const brandsToDisplay = useMemo(() => {
    if (showAllBrands || brands.length <= brandPreviewCount) return brands;
    return brands.slice(0, brandPreviewCount);
  }, [brands, showAllBrands, brandPreviewCount]);
  const canToggleBrands = brands.length > brandPreviewCount;

  const stats = [
    { icon: Award, value: '18+', label: 'Years Experience' },
    { icon: Globe, value: 'PAN India', label: 'Delivery' },
    { icon: Users, value: '500+', label: 'Happy Partners' },
    { icon: Package, value: '4000+', label: 'Products' },
  ];

  const schemaData = [getOrganizationSchema(), getLocalBusinessSchema()];

  return (
    <>
      <SEOHead
        title="Premium Fragrance Wholesaler & Distributor in Ahmedabad"
        description="Silver Perfumes - Authorized wholesale distributor of luxury perfumes, deodorants, cosmetics. Largest exporter of premium fragrance brands in Gujarat since 2013."
        canonical="https://silverperfumes.thevinculum.org.in"
        schemaData={schemaData}
      />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center"
        data-testid="hero-section"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Luxury Perfumes"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="container-custom relative z-10 w-full pt-28 pb-20">
          <div className="max-w-2xl flex flex-col items-start text-left">
            <span className="label-text mb-5 animate-fade-in">
              Authentic Luxury Fragrances Since 2007
            </span>
            <h1 className="heading-xl text-white mb-5 w-full animate-slide-up">
              A legacy of trust in fine perfumery.
            </h1>
            <p
              className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 w-full animate-slide-up"
              style={{ animationDelay: '0.1s', animationFillMode: 'both' }}
            >
              Since 2007, Silver Perfumes has been a trusted wholesale source for authentic designer, niche, and Middle Eastern fragrances—genuine only, without exception.
            </p>
            <div
              className="flex flex-wrap items-center gap-4 animate-slide-up"
              style={{ animationDelay: '0.2s', animationFillMode: 'both' }}
            >
              <button
                onClick={() => setShowQuoteForm(true)}
                className="btn-silver"
                data-testid="hero-quote-btn"
              >
                Request Wholesale Quote
              </button>
              <Link
                to="/products"
                className="btn-secondary border-white text-white hover:bg-white hover:text-[#0A0A0A]"
                data-testid="hero-catalog-btn"
              >
                Explore Catalog
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-[#C0C4CC] rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0A0A0A] py-12" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 text-[#C0C4CC] mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1 font-serif">
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-widest">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="categories-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="label-text mb-4 block">Our Collections</span>
            <h2 className="heading-lg mb-4">Explore by Category</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Discover our comprehensive range of fragrances and beauty products 
              from the world's most prestigious brands.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton h-48" />
              ))}
            </div>
          ) : (
            <div className="bento-grid">
              {categories.slice(0, 6).map((cat, index) => (
                <CategoryCard 
                  key={cat.id} 
                  category={cat} 
                  large={index === 0}
                />
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/products" className="btn-primary" data-testid="view-all-categories">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding bg-white" data-testid="featured-products">
        <div className="container-custom">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="label-text mb-4 block">Featured</span>
              <h2 className="heading-lg">Popular Products</h2>
            </div>
            <Link
              to="/products"
              className="hidden md:inline-flex items-center gap-2 text-[#0A0A0A] font-medium uppercase tracking-widest text-sm hover:text-[#C0C4CC] transition-colors"
            >
              View All
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-72" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 4).map((product) => {
                const category = categories.find(c => c.id === product.category_id);
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    category={category?.name}
                    categorySlug={category?.slug}
                    brand={product.brand_name}
                    brandSlug={product.brand_slug}
                  />
                );
              })}
            </div>
          )}

          <div className="text-center mt-10 md:hidden">
            <Link to="/products" className="btn-primary">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="home-brands-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="label-text mb-4 block">Our Partners</span>
            <h2 className="heading-lg mb-4">Shop by Brand</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Explore authentic collections from the world&apos;s most prestigious fragrance houses.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
              {[...Array(brandPreviewCount)].map((_, i) => (
                <div key={i} className="skeleton h-24" />
              ))}
            </div>
          ) : brands.length > 0 ? (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                {brandsToDisplay.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} />
                ))}
              </div>
              {canToggleBrands && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAllBrands((v) => !v)}
                    data-testid="home-brands-view-all-toggle"
                  >
                    {showAllBrands
                      ? 'Show fewer brands'
                      : `View all brands (${brands.length})`}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-500">Brands coming soon.</p>
          )}
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-white" data-testid="about-preview">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="label-text mb-4 block">About Us</span>
              <h2 className="heading-lg mb-6">
                About Silver Perfumes
              </h2>
              <div className="silver-separator mb-6" />
              <p className="text-gray-600 mb-4 leading-relaxed">
                Born from a journey through Dubai and Thailand, and brought to life in Ahmedabad, Silver Perfumes is a destination for those who appreciate the art of fine fragrance.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Inspired by the richness of Arabian perfumery and the elegance of global scent culture, we offer a refined collection of branded perfumes, Arabic fragrances, premium attars, oud, and more.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Every fragrance we select is chosen to evoke character, confidence, and lasting impression. Silver Perfumes where heritage, luxury, and fragrance come together.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#0A0A0A] font-medium uppercase tracking-widest text-sm hover:text-[#C0C4CC] transition-colors"
                data-testid="about-link"
              >
                Learn More About Us
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src={ABOUT_IMAGE}
                alt="Silver Perfumes store"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0A0A0A] text-white p-6">
                <div className="text-4xl font-serif text-[#C0C4CC] mb-1">18+</div>
                <div className="text-sm uppercase tracking-widest">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#F4F4F5]" data-testid="cta-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="label-text mb-4 block">Get Started</span>
              <h2 className="heading-lg mb-6">
                Ready to Partner<br />With Us?
              </h2>
              <div className="silver-separator mb-6" />
              <p className="text-gray-600 mb-6">
                Whether you're a retailer, wholesaler, or corporate buyer, we're here to 
                help you access premium fragrances at competitive wholesale prices.
              </p>
              <ul className="space-y-3 mb-8">
                {['Authentic products guaranteed', 'Competitive wholesale pricing', 'Fast shipping worldwide', 'Dedicated account manager'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[#C0C4CC] rounded-full" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <LeadForm
                sourcePageUrl="/home"
                title="Request a Quote"
                subtitle="Fill in your details and we'll get back to you within 24 hours"
                showBuyerType={true}
              />
            </div>
          </div>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl="/home"
        showBuyerType={true}
        title="Request Wholesale Quote"
        subtitle="Tell us about your business and we'll provide customized pricing"
      />
    </>
  );
}
